import React, { useState, useEffect } from "react";
import { Table, Button, Input, Spin, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { getRequest } from "../../../../service";
import { getEmployeeId, getEmployeeData } from "../../../../utils/localStorage";
import { formatDate } from "../../../../utils/dateTime";
import {Wallet, Calculator, Coins, CalendarDays, Eye } from "lucide-react";

const employeeData = getEmployeeData();
const currencySign = employeeData?.currency ?? "PKR";

const columns = [
  {
    title: "Employee",
    key: "employee",
    render: (_, record) => `${record.employeeId?.firstName || ""} ${record.employeeId?.lastName || ""}`.trim() || "N/A"
  },
  {
    title: "Email",
    key: "email",
    responsive: ["lg"],
    render: (_, record) => record.employeeId?.emailAddress || "N/A"
  },
  {
    title: "Company",
    dataIndex: "companyName",
    key: "companyName",
    responsive: ["md"],
    render: (_, record) => record.companyId?.companyName || "N/A"
  },
  {
    title: "Loan Date",
    dataIndex: "loanDate",
    key: "loanDate",
    responsive: ["md"],
    render: (date) => formatDate(date)
  },
  {
    title: "Months",
    dataIndex: "numberOfMonths",
    key: "numberOfMonths",
    responsive: ["lg"]
  },
  {
    title: "Monthly Installment",
    dataIndex: "perMonthInstalment",
    key: "perMonthInstalment",
    responsive: ["lg"],
    render: (amount) => `${currencySign} ${amount?.toLocaleString()}`
  },
  {
    title: "Loan Amount",
    dataIndex: "loanAmount",
    key: "loanAmount",
    render: (amount) => `${currencySign} ${amount?.toLocaleString()}`
  },
  {
    title: "Total Recovery",
    dataIndex: "totalRecovery",
    key: "totalRecovery",
    responsive: ["lg"],
    render: (amount) => `${currencySign} ${amount?.toLocaleString()}`
  },
  {
    title: "Paid Installments",
    key: "paidInstallments",
    responsive: ["lg"],
    render: (_, record) => {
      const count = record.paidInstalments?.length || 0;
      const totalPaid = record.paidInstalments?.reduce((sum, inst) => sum + (inst.amount || 0), 0) || 0;
      return (
        <div className="text-xs">
          <div className="font-medium">{count} payments</div>
          <div className="text-gray-500">{currencySign} {totalPaid.toLocaleString()}</div>
        </div>
      );
    }
  },
  {
    title: "Approved By",
    dataIndex: "approvedBy",
    key: "approvedBy",
    responsive: ["lg"],
    render: (approvedBy) => approvedBy || "Pending"
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${status === 'completed' ? 'bg-green-100 text-green-800' :
          status === 'active' ? 'bg-blue-100 text-blue-800' :
            status === 'defaulted' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
        }`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    )
  },
  {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <div className="flex gap-2">
        <Button type="link" size="small" icon={<Eye size={16} />}></Button>
      </div>
    ),
  },
];

const LoansAndPayments = () => {
  const [loading, setLoading] = useState(false);
  const [loanData, setLoanData] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  

  useEffect(() => {
    fetchLoanData();
    fetchStats();
  }, []);

  const fetchLoanData = async () => {
    try {
      setLoading(true);
      const companyId = getEmployeeId();
      const response = await getRequest(`/api/loanpayment`);

      if (response.success) {
        setLoanData(response.data);
      } else {
        message.error("Failed to fetch loan data");
      }
    } catch (error) {
      console.error("Error fetching loan data:", error);
      message.error("Error fetching loan data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getRequest("/api/loanpayment/stats");
      console.log(response);
      if (response.success) {
        setStats(response.data);
      } else {
        message.error("Failed to fetch stats");
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      message.error("Error fetching stats");
    }
  };

  const filteredData = loanData.filter(item => {
    const employeeName = `${item.employeeId?.firstName || ""} ${item.employeeId?.lastName || ""}`.toLowerCase();
    const companyName = item.companyId?.companyName?.toLowerCase() || "";
    const searchLower = searchTerm.toLowerCase();

    return employeeName.includes(searchLower) ||
      companyName.includes(searchLower) ||
      item.employeeId?._id?.includes(searchLower);
  });

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="bg-green-50 p-3 md:p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-600 text-sm">Total Loans</p>
              <h3 className="text-lg md:text-xl font-bold mt-1">{stats.totalLoans || 0}</h3>
            </div>
            <Calculator className="h-6 w-6 text-purple-600" />
          </div>
        </div>
        <div className="bg-blue-50 p-3 md:p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-600 text-sm">Active Loans</p>
              <h3 className="text-lg md:text-xl font-bold mt-1">{stats.activeLoans || 0}</h3>
            </div>
            <CalendarDays className="h-6 w-6 text-blue-600" />
          </div>
        </div>
        <div className="bg-purple-50 p-3 md:p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-600 text-sm">Total Amount</p>
              <h3 className="text-lg md:text-xl font-bold mt-1">{currencySign} {stats.totalAmount?.toLocaleString() || 0}</h3>
            </div>
            
            <Wallet className="h-6 w-6 text-green-600" />
          </div>
        </div>
        <div className="bg-orange-50 p-3 md:p-4 rounded-lg shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-600 text-sm">Remaining</p>
              <h3 className="text-lg md:text-xl font-bold mt-1">{currencySign} {stats.totalRemaining?.toLocaleString() || 0}</h3>
            </div>
            <Coins className="h-6 w-6 text-orange-600" />
          </div>
        </div>
      </div>
      <div className="p-3 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_-4px_6px_rgba(0,0,0,0.05)] rounded-md">


        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
          <div className="flex w-full max-w-md gap-4 items-center">
            <Input
              placeholder="Search by employee name, company, or ID"
              className="w-full text-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="rounded mt-6 overflow-x-auto">
          <Table
            columns={columns}
            dataSource={filteredData}
            locale={{ emptyText: "No loan data found" }}
            scroll={{ x: 'max-content' }}
            loading={loading}
            pagination={{
              total: filteredData.length,
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
            }}
            className="bg-white rounded-lg border border-gray-200"
            rowKey="_id"
          />
        </div>

      </div>
    </>
  );
};

export default LoansAndPayments;
